import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * Which downstream services this browser is routing to an ephemeral PR environment.
 *
 * Each entry becomes an `x-pr-env-<appLabel>: pr-<N>` header on every request the panel makes.
 * support-gateway forwards those headers onto its own internal calls, and each service's Istio
 * VirtualService forks the ones addressed to it (RFC-012 §2, INF-856). TST only.
 *
 * ## Why this is persisted state and not URL state
 *
 * CLAUDE.md requires view state to live in the URL. This is a deliberate exception, and the reasons
 * are the same ones behind that rule rather than in spite of them:
 *
 * - It is not view state. It does not change *what* a screen shows, it changes *which deployment
 *   answers*. Nothing in the rendered rows is derived from it.
 * - The rule exists so a link can be pasted into Slack and reproduce what you see. Here that would
 *   be actively harmful: the reader's panel would silently start talking to your half-finished pod.
 *   A link should reproduce a view, not hijack a backend.
 * - It is global and must survive route changes, reloads and a whole shift. `useUrlState` is
 *   per-view, so every route would have to re-apply it and any view that rebuilds its params would
 *   drop it.
 * - PROD is a different origin from TST (`panel.backofficeap.com` vs `panel.testingap.com`), so
 *   localStorage physically cannot carry a TST configuration into PROD. A URL can be pasted across
 *   origins; this cannot.
 */
export interface IPrEnvEntry {
  /** The `app.<key>.url` config key the backend reported, e.g. `kyc-api`. Display only. */
  key: string;
  /**
   * The ArgoCD application label — what the header is built from, e.g. `kyc`.
   *
   * Always take it from the targets endpoint. It is derivable from neither the config key nor the
   * hostname (`app.users-api.url` at `users-api.internal…` is app `users`), and a wrong one matches
   * no VirtualService, so the call lands on stable and reads as the PR being broken.
   */
  appLabel: string;
  /** Human-readable name for the list, e.g. `Kyc`. */
  label: string;
  /** PR number, stored bare; the `pr-` prefix is applied when the header is built. */
  pr: number;
  /**
   * True for support-gateway itself, which forks only the panel's API calls — the UI keeps loading
   * from stable. Optional, and deliberately not a store-version bump: an entry persisted before this
   * field existed simply loses the hint, which is not worth wiping a working configuration for.
   */
  self?: boolean;
  /** When this entry was added, so the UI can warn about the 72h ephemeral-env reap. */
  addedAt: number;
}

interface PrEnvState {
  version: number;
  entries: IPrEnvEntry[];
  /** Adds an entry, or replaces the existing one for the same service. */
  setEntry: (entry: Omit<IPrEnvEntry, 'addedAt'>) => void;
  removeEntry: (appLabel: string) => void;
  clearAll: () => void;
}

const STORE_KEY = 'ap_pr_env_store_v1';

/**
 * One header per entry on every request, so this bounds both header size and how much traffic an
 * operator can point at half-built pods in one go. The backend advertises the same cap.
 */
export const MAX_PR_ENV_ENTRIES = 8;

/**
 * Bumped to 2 because v1 stored app labels the first cut of this feature derived from the hostname,
 * and a third of them were wrong (`users-api` for what is app `users`, `wallet` for `wallets`). Those
 * entries send a header no VirtualService matches, so anything persisted under v1 is discarded rather
 * than migrated — there is nothing to map it back from, and a silent no-op is the worst outcome here.
 */
const STORE_VERSION = 2;

const EMPTY_STATE = { version: STORE_VERSION, entries: [] as IPrEnvEntry[] };

/**
 * localStorage, or an in-memory stand-in when there isn't one.
 *
 * zustand's default storage assumes a `localStorage` global, and dereferences it unguarded on the
 * first write. There is none under the test environment, and none in a browser that has blocked
 * storage — in both cases the store should lose its persistence, not throw on every mutation.
 */
const safeStorage = createJSONStorage(() => {
  try {
    if (typeof localStorage !== 'undefined') return localStorage;
  } catch {
    // Access itself throws in some privacy modes.
  }
  const memory = new Map<string, string>();
  return {
    getItem: (name: string) => memory.get(name) ?? null,
    setItem: (name: string, value: string) => {
      memory.set(name, value);
    },
    removeItem: (name: string) => {
      memory.delete(name);
    },
  };
});

export const usePrEnvStore = create<PrEnvState>()(
  persist(
    (set) => ({
      ...EMPTY_STATE,
      setEntry: (entry) =>
        set((s) => {
          const existing = s.entries.find((e) => e.appLabel === entry.appLabel);
          const next: IPrEnvEntry = { ...entry, addedAt: Date.now() };
          // Replace rather than reject: only one x-pr-env-<appLabel> header can exist per service,
          // and "I pushed a new commit, new PR number" is the common case.
          if (existing) {
            return { entries: s.entries.map((e) => (e.appLabel === entry.appLabel ? next : e)) };
          }
          if (s.entries.length >= MAX_PR_ENV_ENTRIES) return s;
          return { entries: [...s.entries, next] };
        }),
      removeEntry: (appLabel) =>
        set((s) => ({ entries: s.entries.filter((e) => e.appLabel !== appLabel) })),
      clearAll: () => set({ entries: [] }),
    }),
    {
      name: STORE_KEY,
      version: STORE_VERSION,
      storage: safeStorage,
      migrate: (persistedState: unknown) => {
        if (!persistedState) return persistedState as unknown;
        const state = persistedState as Partial<PrEnvState> & { version?: number };
        if (state.version === STORE_VERSION) return persistedState as PrEnvState;
        // Wipe incompatible state: a stale routing entry is worse than none.
        return EMPTY_STATE as PrEnvState;
      },
    }
  )
);

/**
 * Builds the outbound header map.
 *
 * Names are lowercase because commons-spring matches the configured prefix with a case-sensitive
 * `startsWith`, so `X-PR-Env-Kyc` would be captured by nothing and forwarded nowhere. This is the
 * single place the `pr-` prefix is applied.
 */
export const buildPrEnvHeaders = (entries: IPrEnvEntry[]): Record<string, string> =>
  entries.reduce<Record<string, string>>((headers, entry) => {
    headers[`x-pr-env-${entry.appLabel.toLowerCase()}`] = `pr-${entry.pr}`;
    return headers;
  }, {});

const MAX_PR_NUMBER = 999999;

/**
 * Parses what an operator actually pastes.
 *
 * Argo shows `pr-336`, GitHub shows `#336`, and either is a reasonable thing to copy — rejecting
 * them as invalid would be gratuitous.
 *
 * @returns the PR number, or null when the input is not one
 */
export const parsePrNumber = (raw: string): number | null => {
  const cleaned = raw
    .trim()
    .replace(/^#/, '')
    .replace(/^pr[-\s]?/i, '');
  if (!/^\d+$/.test(cleaned)) return null;
  const parsed = Number(cleaned);
  return parsed >= 1 && parsed <= MAX_PR_NUMBER ? parsed : null;
};

export default usePrEnvStore;
